import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { eventApi } from '../services/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getStatusLabel(status) {
  const map = {
    registration_open: '报名中',
    registration_closed: '报名截止',
    in_progress: '进行中',
    completed: '已结束',
    finished: '已结束',
    draft: '草稿',
  };
  return map[status] || status;
}

function getStatusColor(status) {
  const map = {
    registration_open: 'success',
    in_progress: 'warning',
    completed: 'default',
    finished: 'default',
    registration_closed: 'info',
  };
  return map[status] || 'default';
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    eventApi
      .getList({ page, limit: 9 })
      .then((data) => {
        if (Array.isArray(data)) {
          setEvents(data);
          setTotalPages(1);
        } else if (data && data.tournaments) {
          setEvents(data.tournaments);
          setTotalPages(data.pagination?.pages || 1);
        } else {
          setEvents([]);
        }
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* 页头 */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h2" sx={{ mb: 1 }}>赛事中心</Typography>
          <Typography variant="body1" color="text.secondary">
            浏览最新赛事信息，在中象在线APP或小程序中报名参赛
          </Typography>
        </Box>

        {/* 列表 */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography color="text.secondary">暂无赛事</Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 3,
              }}
            >
              {events.map((event) => (
                <Card
                  key={event._id}
                  sx={{
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
                  }}
                >
                  {event.posterFile?.cosUrl && (
                    <CardMedia
                      component="img"
                      height={200}
                      image={event.posterFile.cosUrl}
                      alt={event.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem', flex: 1, mr: 1 }}>
                        {event.title}
                      </Typography>
                      <Chip
                        label={getStatusLabel(event.status)}
                        color={getStatusColor(event.status)}
                        size="small"
                      />
                    </Box>

                    <Stack spacing={0.8}>
                      {event.startTime && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                          <CalendarTodayIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2">
                            {formatDate(event.startTime)}
                            {event.endTime && event.endTime !== event.startTime && ` ~ ${formatDate(event.endTime)}`}
                          </Typography>
                        </Stack>
                      )}
                      {event.location?.name && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                          <LocationOnIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" noWrap>{event.location.name}</Typography>
                        </Stack>
                      )}
                    </Stack>

                  </CardContent>
                </Card>
              ))}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
